import React, { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AgentSessionHistoryView } from "./discovery/AgentSessionHistoryView";
import {
  Upload,
  ArrowLeft,
  ArrowRight,
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  Download,
  FileSpreadsheet,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  X,
  Send,
  Loader2,
  Check,
  Edit3,
  ChevronUp,
  FileText,
  BrainCircuit,
  Target,
  ListChecks,
  Plus,
  Trash2,
  Save,
  ExternalLink,
  ClipboardList,
  Hash,
  AlertCircle,
  Zap,
  MessageSquare,
  Eye,
  Clock,
  Play,
  FolderOpen,
} from "lucide-react";
import { api } from "../api";
import type {
  BacklogRequirement,
  BacklogClarification,
  BacklogEpic,
  BacklogStory,
  BacklogUploadResponse,
  BacklogSessionSummary,
  BacklogResumeResponse,
  JiraExportConfig,
  GithubExportConfig,
} from "../api";
import GreenTendrils from "./GreenTendrils";

interface Props {
  onBack: () => void;
  projectId?: string;
}

/* ── Step indicator ── */
const STEPS = [
  { label: "Upload BRD", icon: Upload },
  { label: "AI Analysis & Clarification", icon: BrainCircuit },
  { label: "Epic Design", icon: Target },
  { label: "Story Generation", icon: ListChecks },
  { label: "Review & Edit", icon: Edit3 },
  { label: "Export", icon: Download },
];

function StepIndicator({ current }: { current: number }) {
  return (
    <div className="flex items-center gap-1">
      {STEPS.map((s, i) => {
        const Icon = s.icon;
        const isActive = i + 1 === current;
        const isDone = i + 1 < current;
        return (
          <div key={i} className="flex items-center">
            <div
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                isActive
                  ? "bg-brand-500/20 text-brand-500 border border-brand-500/30"
                  : isDone
                    ? "bg-green-500/10 text-green-400"
                    : "bg-surface-200 text-white/25"
              }`}
            >
              {isDone ? <CheckCircle2 size={12} /> : <Icon size={12} />}
              <span className="hidden sm:inline">{s.label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <ChevronRight size={12} className="text-white/10 mx-1" />
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ── Priority badge ── */
function PriorityBadge({ priority }: { priority: string }) {
  const map: Record<string, { bg: string; text: string }> = {
    must: { bg: "bg-red-500/15 border-red-500/30", text: "text-red-400" },
    should: {
      bg: "bg-orange-500/15 border-orange-500/30",
      text: "text-orange-400",
    },
    could: { bg: "bg-blue-500/15 border-blue-500/30", text: "text-blue-400" },
    wont: { bg: "bg-white/5 border-white/10", text: "text-white/40" },
  };
  const s = map[priority] || map.could;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-bold border uppercase ${s.bg} ${s.text}`}
    >
      {priority}
    </span>
  );
}

/* ── Story point badge ── */
function StoryPointBadge({
  points,
  confidence,
}: {
  points: number;
  confidence: string;
}) {
  const colorMap: Record<string, string> = {
    high: "bg-green-500/20 text-green-400 border-green-500/30",
    medium: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    low: "bg-red-500/20 text-red-400 border-red-500/30",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold border ${colorMap[confidence] || colorMap.medium}`}
    >
      <Hash size={10} /> {points} pts
    </span>
  );
}

/* ── Requirement type badge ── */
function RequirementTypeBadge({ type }: { type: string }) {
  const colors: Record<string, string> = {
    functional: "bg-blue-500/15 text-blue-400 border-blue-500/30",
    "non-functional": "bg-brand-500/15 text-brand-500 border-brand-500/30",
    constraint: "bg-orange-500/15 text-orange-400 border-orange-500/30",
    assumption: "bg-teal-500/15 text-teal-400 border-teal-500/30",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-bold border ${colors[type] || colors.functional}`}
    >
      {type.replace("-", " ")}
    </span>
  );
}

/* ─────────────────────────────────────────────────────── */
export default function BacklogArchitectView({ onBack, projectId }: Props) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Session
  const [sessionId, setSessionId] = useState<string | null>(null);

  // Step 1: Upload
  const [dragOver, setDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [additionalContext, setAdditionalContext] = useState("");
  const [textOnlyMode, setTextOnlyMode] = useState(false);
  const [requirementsText, setRequirementsText] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Step 2: Analysis & Clarification
  const [requirements, setRequirements] = useState<BacklogRequirement[]>([]);
  const [clarifications, setClarifications] = useState<BacklogClarification[]>(
    [],
  );
  const [clarificationAnswers, setClarificationAnswers] = useState<
    Record<string, string>
  >({});
  const [documentSummary, setDocumentSummary] = useState("");
  const [reqCounts, setReqCounts] = useState({
    functional: 0,
    non_functional: 0,
    constraints: 0,
    assumptions: 0,
  });

  // Step 3: Epics
  const [epics, setEpics] = useState<BacklogEpic[]>([]);
  const [coverageMap, setCoverageMap] = useState<Record<string, string[]>>({});
  const [epicFeedback, setEpicFeedback] = useState("");
  const [epicSelections, setEpicSelections] = useState<Record<string, boolean>>(
    {},
  );
  const [editingEpicId, setEditingEpicId] = useState<string | null>(null);
  const [editEpicForm, setEditEpicForm] = useState<{
    title: string;
    goal: string;
  }>({ title: "", goal: "" });
  const [projectRoles, setProjectRoles] = useState<string[]>([]);
  const [roleInput, setRoleInput] = useState("");

  // Step 4-5: Stories & Review
  const [expandedEpic, setExpandedEpic] = useState<string | null>(null);
  const [expandedStory, setExpandedStory] = useState<string | null>(null);
  const [editingStory, setEditingStory] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<BacklogStory>>({});
  const [storyGenProgress, setStoryGenProgress] = useState<{
    current: number;
    total: number;
    epicKey: string;
  }>({ current: 0, total: 0, epicKey: "" });

  // Step 5: Chat
  const [chatMessages, setChatMessages] = useState<
    { role: string; content: string }[]
  >([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Step 6: Export
  const [exportFormat, setExportFormat] = useState<string>("excel");
  const [jiraConfig, setJiraConfig] = useState<JiraExportConfig>({
    base_url: "",
    email: "",
    api_token: "",
    project_key: "",
  });
  const [githubConfig, setGithubConfig] = useState<GithubExportConfig>({
    token: "",
    owner: "",
    repo: "",
  });
  const [exportResult, setExportResult] = useState<{
    status: string;
    items_created: number;
    details: { key: string; title: string; url: string }[];
    errors?: string[];
  } | null>(null);

  // Scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  // Saved sessions
  const [savedSessions, setSavedSessions] = useState<BacklogSessionSummary[]>(
    [],
  );
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [showSavedSessions, setShowSavedSessions] = useState(false);

  // Load saved sessions on mount (v2 backlog is project-scoped)
  useEffect(() => {
    if (!projectId) return;
    const loadSessions = async () => {
      try {
        setLoadingSessions(true);
        const data = await api.backlogListSessions(projectId);
        setSavedSessions(data.sessions);
        if (data.sessions.length > 0) setShowSavedSessions(true);
      } catch {
        // ignore — no saved sessions
      } finally {
        setLoadingSessions(false);
      }
    };
    loadSessions();
  }, [projectId]);

  /* ── Resume a saved session ── */
  const handleResumeSession = useCallback(async (sid: string) => {
    setError(null);
    setLoading(true);
    try {
      const data: BacklogResumeResponse = await api.backlogResumeSession(sid);
      setSessionId(data.session_id);
      setRequirements(data.requirements);
      setClarifications(data.clarifications);
      setDocumentSummary(data.document_summary);
      setReqCounts(data.requirement_count);
      setEpics(data.epics);
      setCoverageMap(data.coverage_map);
      setProjectRoles(data.project_roles || []);
      setAdditionalContext(data.additional_context || "");
      // Set epic selections (all selected by default)
      const sel: Record<string, boolean> = {};
      data.epics.forEach((e: BacklogEpic) => {
        sel[e.id] = true;
      });
      setEpicSelections(sel);
      // Jump to the right UI step
      setStep(data.ui_step);
    } catch (err: any) {
      setError(err?.message || "Failed to resume session");
    } finally {
      setLoading(false);
    }
  }, []);

  /* ── Delete a saved session ── */
  const handleDeleteSession = useCallback(async (sid: string) => {
    try {
      await api.backlogDeleteSession(sid);
      setSavedSessions((prev) => prev.filter((s) => s.session_id !== sid));
    } catch (err: any) {
      setError(err?.message || "Failed to delete session");
    }
  }, []);

  /* ── Step 1: Upload & Parse ── */
  const handleFileDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) setSelectedFile(file);
  }, []);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) setSelectedFile(file);
    },
    [],
  );

  const handleUpload = useCallback(async () => {
    setError(null);
    setLoading(true);
    const hydrate = (d: BacklogUploadResponse) => {
      setSessionId(d.session_id);
      setRequirements(d.requirements);
      setClarifications(d.clarifications);
      setDocumentSummary(d.document_summary);
      setReqCounts(d.requirement_count);
      setStep(2);
    };
    try {
      if (textOnlyMode && requirementsText.trim()) {
        if (!projectId) {
          throw new Error(
            "No project selected — open the Backlog Architect from a project.",
          );
        }
        hydrate(
          await api.backlogAnalyzeText(
            projectId,
            requirementsText,
            additionalContext || undefined,
          ),
        );
      } else if (selectedFile) {
        if (!projectId) {
          throw new Error(
            "No project selected — open the Backlog Architect from a project.",
          );
        }
        const res = await api.backlogUpload(
          projectId,
          selectedFile,
          additionalContext || undefined,
        );
        if (res.mode === "async") {
          // Every upload now parses + analyzes in the worker (not just
          // .docx/.pdf) — poll the job, then hydrate from its result (same
          // shape as the old sync analyze payload). The job can sit
          // "queued" for a while before analysis even starts if a
          // secondary malware scan hasn't cleared yet (worker-side retry
          // budget ~10 min — 20 retries × 30s, see
          // ztd_app/worker/tasks/backlog_documents.py's
          // _SECONDARY_SCAN_MAX_RETRIES — plus whatever the LLM analyze
          // step itself then takes). Poll for 15 min, matching api.ts's
          // pollUntil() default used by the other three scan-gated
          // wizards, so this can't time out on the frontend before the
          // backend's own retry budget is exhausted.
          let analyzed: BacklogUploadResponse | null = null;
          for (let i = 0; i < 600; i++) {
            await new Promise((r) => setTimeout(r, 1500));
            const job = await api.backlogGetJob(res.job_id);
            if (job.status === "completed") {
              analyzed = job.result as unknown as BacklogUploadResponse;
              break;
            }
            if (job.status === "failed" || job.status === "quarantined") {
              throw new Error(
                job.error_detail ||
                  (job.status === "quarantined"
                    ? "This file was flagged by a malware scan and has been quarantined for review."
                    : "Document analysis failed"),
              );
            }
          }
          if (!analyzed) {
            throw new Error("Document analysis timed out — please try again.");
          }
          hydrate(analyzed);
        } else {
          hydrate(res);
        }
      } else {
        setError("Please select a file or enter requirements text");
        setLoading(false);
        return;
      }
    } catch (err: any) {
      setError(err?.message || "Upload failed");
    } finally {
      setLoading(false);
    }
  }, [
    selectedFile,
    additionalContext,
    textOnlyMode,
    requirementsText,
    projectId,
  ]);

  /* ── Step 2: Clarification ── */
  const generateEpicsAfterClarify = useCallback(async (sid: string) => {
    try {
      const epicData = await api.backlogGenerateEpics(sid);
      setEpics(epicData.epics);
      setCoverageMap(epicData.coverage_map);
      setStep(3);
    } catch (err: any) {
      setStep(3);
      setError(err?.message || "Epic generation failed");
    }
  }, []);

  const handleSubmitClarifications = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.backlogClarify(sessionId, clarificationAnswers);
      setRequirements(data.requirements);
      // Filter follow-ups to only valid ones with actual question text
      const validFollowUps = (data.follow_up_clarifications || []).filter(
        (q: BacklogClarification) => q.question && q.question.trim().length > 0,
      );
      if (validFollowUps.length > 0 && !data.all_clarified) {
        // Ensure each follow-up has required fields
        const normalised = validFollowUps.map(
          (q: BacklogClarification, idx: number) => ({
            id: q.id || `followup-${idx + 1}`,
            question: q.question,
            context: q.context || "",
            category: q.category || "requirement",
          }),
        );
        setClarifications(normalised);
        setClarificationAnswers({});
      } else {
        setClarifications([]);
        await generateEpicsAfterClarify(sessionId);
      }
    } catch (err: any) {
      setError(err?.message || "Clarification failed");
    } finally {
      setLoading(false);
    }
  }, [sessionId, clarificationAnswers, generateEpicsAfterClarify]);

  const handleSkipClarifications = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    setError(null);
    setClarifications([]);
    await generateEpicsAfterClarify(sessionId);
    setLoading(false);
  }, [sessionId, generateEpicsAfterClarify]);

  /* ── Step 3: Generate Epics ── */
  const handleGenerateEpics = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.backlogGenerateEpics(sessionId);
      setEpics(data.epics);
      setCoverageMap(data.coverage_map);
      const sel: Record<string, boolean> = {};
      data.epics.forEach((e: BacklogEpic) => {
        sel[e.id] = true;
      });
      setEpicSelections(sel);
      setStep(3);
    } catch (err: any) {
      setError(err?.message || "Epic generation failed");
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  /* ── Step 4: Generate Stories ── */
  const selectedEpicIds = Object.entries(epicSelections)
    .filter(([, v]) => v)
    .map(([k]) => k);

  const handleAddRole = useCallback(() => {
    const trimmed = roleInput.trim();
    if (trimmed && !projectRoles.includes(trimmed)) {
      setProjectRoles((prev) => [...prev, trimmed]);
      setRoleInput("");
    }
  }, [roleInput, projectRoles]);

  const handleRemoveRole = useCallback((role: string) => {
    setProjectRoles((prev) => prev.filter((r) => r !== role));
  }, []);

  const handleGenerateAllStories = useCallback(async () => {
    if (!sessionId) return;
    const epicIds = selectedEpicIds;
    const targetEpics = epics.filter((e) => epicIds.includes(e.id));
    if (targetEpics.length === 0) return;

    setStep(4);
    setLoading(true);
    setError(null);
    const errors: string[] = [];
    const updatedEpics = [...epics];

    for (let i = 0; i < targetEpics.length; i++) {
      const epic = targetEpics[i];
      setStoryGenProgress({
        current: i + 1,
        total: targetEpics.length,
        epicKey: epic.key,
      });
      try {
        const data = await api.backlogGenerateStories(
          sessionId,
          epic.id,
          epicFeedback || undefined,
          projectRoles.length > 0 ? projectRoles : undefined,
        );
        // Merge stories for this epic into our local state
        const returnedEpic = data.epics.find((e) => e.id === epic.id);
        if (returnedEpic) {
          const idx = updatedEpics.findIndex((e) => e.id === epic.id);
          if (idx !== -1) {
            updatedEpics[idx] = {
              ...updatedEpics[idx],
              stories: returnedEpic.stories,
              total_points: returnedEpic.total_points,
            };
          }
        }
      } catch (err: any) {
        errors.push(`${epic.key}: ${err?.message || "Failed"}`);
      }
    }

    // Keep only epics that have stories (selected ones that generated successfully)
    const finalEpics = updatedEpics.filter(
      (e) => e.stories && e.stories.length > 0,
    );
    setEpics(finalEpics);

    if (errors.length > 0 && errors.length === targetEpics.length) {
      setError("Story generation failed for all epics: " + errors.join("; "));
      setStep(3);
    } else {
      if (errors.length > 0) {
        setError("Some epics had issues: " + errors.join("; "));
      }
      setStep(5);
    }
    setLoading(false);
  }, [sessionId, epicFeedback, selectedEpicIds, epics, projectRoles]);

  /* ── Step 5: Edit Story ── */
  const handleSaveStory = useCallback(
    async (storyId: string) => {
      if (!sessionId) return;
      setLoading(true);
      try {
        const data = await api.backlogUpdateStory(sessionId, storyId, editForm);
        // Update local state
        setEpics((prev) =>
          prev.map((epic) => ({
            ...epic,
            stories: epic.stories.map((s) =>
              s.id === storyId ? data.story : s,
            ),
          })),
        );
        setEditingStory(null);
        setEditForm({});
      } catch (err: any) {
        setError(err?.message || "Save failed");
      } finally {
        setLoading(false);
      }
    },
    [sessionId, editForm],
  );

  const handleDeleteStory = useCallback(
    async (storyId: string) => {
      if (!sessionId) return;
      try {
        await api.backlogDeleteStory(sessionId, storyId);
        setEpics((prev) =>
          prev.map((epic) => ({
            ...epic,
            stories: epic.stories.filter((s) => s.id !== storyId),
            total_points: epic.stories
              .filter((s) => s.id !== storyId)
              .reduce((sum, s) => sum + s.story_points, 0),
          })),
        );
      } catch (err: any) {
        setError(err?.message || "Delete failed");
      }
    },
    [sessionId],
  );

  /* ── Chat ── */
  const handleChatSend = useCallback(async () => {
    if (!sessionId || !chatInput.trim()) return;
    const msg = chatInput.trim();
    setChatInput("");
    setChatMessages((prev) => [...prev, { role: "user", content: msg }]);
    setChatLoading(true);
    try {
      const data = await api.backlogChat(sessionId, msg);
      setChatMessages((prev) => [
        ...prev,
        { role: "assistant", content: data.response },
      ]);
      if (data.updated_epics) {
        setEpics(data.updated_epics);
      }
    } catch (err: any) {
      setChatMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `Error: ${err?.message || "Chat failed"}`,
        },
      ]);
    } finally {
      setChatLoading(false);
    }
  }, [sessionId, chatInput]);

  /* ── Export ── */
  const handleExport = useCallback(async () => {
    if (!sessionId) return;
    setError(null);
    setExportResult(null);

    if (exportFormat === "excel") {
      window.open(api.backlogExportExcel(sessionId), "_blank");
      return;
    }
    if (exportFormat === "word") {
      window.open(api.backlogExportWord(sessionId), "_blank");
      return;
    }

    setLoading(true);
    try {
      if (exportFormat === "jira") {
        const result = await api.backlogExportJira(sessionId, jiraConfig);
        setExportResult(result);
      } else if (exportFormat === "github") {
        const result = await api.backlogExportGithub(sessionId, githubConfig);
        setExportResult(result);
      }
    } catch (err: any) {
      setError(err?.message || "Export failed");
    } finally {
      setLoading(false);
    }
  }, [sessionId, exportFormat, jiraConfig, githubConfig]);

  /* ── Totals ── */
  const totalStories = epics.reduce((sum, e) => sum + e.stories.length, 0);
  const totalPoints = epics.reduce((sum, e) => sum + e.total_points, 0);

  if (searchParams.get("tab") === "history" && projectId) {
    return (
      <AgentSessionHistoryView
        projectId={projectId}
        sessionType="backlog"
        agentLabel="Backlog Architect"
        onResume={(id) => navigate(`?session=${id}`)}
        onNewSession={() => navigate("?")}
      />
    );
  }

  return (
    <div className="relative min-h-screen">
      <GreenTendrils />

      <div className="relative z-10 max-w-6xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 rounded-lg hover:bg-surface-200 text-white/40 hover:text-white transition"
            >
              <ArrowLeft size={18} />
            </button>
            <div>
              <h1 className="text-2xl font-bold">
                <span className="gradient-text-green">AI Backlog</span>
                <span className="text-white/40 font-extralight">
                  {" "}
                  Architect
                </span>
              </h1>
              <p className="text-xs text-white/30 mt-0.5">
                BMAD-Powered Agile Backlog Generation
              </p>
            </div>
          </div>
          <StepIndicator current={step} />
        </div>

        {/* Error banner */}
        {error && (
          <div className="mb-6 p-3 bg-red-500/10 border border-red-500/30 rounded-2xl text-sm text-red-300 flex items-center justify-between">
            <span className="flex items-center gap-2">
              <AlertCircle size={14} /> {error}
            </span>
            <button
              onClick={() => setError(null)}
              className="text-red-400 hover:text-red-300 text-xs"
            >
              dismiss
            </button>
          </div>
        )}

        {/* ═══════ STEP 1: Upload BRD ═══════ */}
        {step === 1 && (
          <div className="space-y-6">
            {/* Mode toggle */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setTextOnlyMode(false)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition ${!textOnlyMode ? "bg-brand-500/20 text-brand-500 border border-brand-500/40" : "bg-white/5 text-white/40 border border-white/10 hover:bg-white/10"}`}
              >
                <Upload size={14} className="inline mr-2" /> Upload Document
              </button>
              <button
                onClick={() => setTextOnlyMode(true)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition ${textOnlyMode ? "bg-brand-500/20 text-brand-500 border border-brand-500/40" : "bg-white/5 text-white/40 border border-white/10 hover:bg-white/10"}`}
              >
                <FileText size={14} className="inline mr-2" /> Paste
                Requirements
              </button>
            </div>

            {!textOnlyMode ? (
              /* File upload zone */
              <div
                onDrop={handleFileDrop}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all duration-300 ${
                  dragOver
                    ? "border-brand-500/60 bg-brand-500/10"
                    : selectedFile
                      ? "border-brand-500/30 bg-brand-500/5"
                      : "border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/5"
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".docx,.xlsx,.xls,.pdf,.txt,.md"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                {selectedFile ? (
                  <div className="space-y-2">
                    <FileSpreadsheet
                      size={40}
                      className="mx-auto text-brand-500"
                    />
                    <p className="text-sm text-white/80 font-medium">
                      {selectedFile.name}
                    </p>
                    <p className="text-xs text-white/30">
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </p>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFile(null);
                      }}
                      className="text-xs text-red-400 hover:text-red-300"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Upload size={40} className="mx-auto text-white/20" />
                    <p className="text-sm text-white/40">
                      Drag & drop your BRD, PRD, or requirements document
                    </p>
                    <p className="text-xs text-white/20">
                      Supports: .docx, .xlsx, .pdf, .txt, .md
                    </p>
                  </div>
                )}
              </div>
            ) : (
              /* Text input zone */
              <textarea
                value={requirementsText}
                onChange={(e) => setRequirementsText(e.target.value)}
                placeholder="Paste your business requirements, feature descriptions, or user needs here..."
                className="w-full h-64 bg-surface-200 border border-white/10 rounded-2xl p-4 text-sm text-white/80 placeholder-white/20 resize-none focus:border-brand-500/40 focus:outline-none transition"
              />
            )}

            {/* Additional context */}
            <div>
              <label className="block text-xs text-white/30 mb-2">
                Additional Context (optional)
              </label>
              <textarea
                value={additionalContext}
                onChange={(e) => setAdditionalContext(e.target.value)}
                placeholder="Describe the tech stack, constraints, team size, target audience, or any other relevant context..."
                className="w-full h-24 bg-surface-200 border border-white/10 rounded-2xl p-4 text-sm text-white/80 placeholder-white/20 resize-none focus:border-brand-500/40 focus:outline-none transition"
              />
            </div>

            <div className="flex justify-end">
              <button
                onClick={handleUpload}
                disabled={
                  loading ||
                  (!selectedFile && !textOnlyMode) ||
                  (textOnlyMode && !requirementsText.trim())
                }
                className="px-6 py-3 bg-brand-500 hover:bg-brand-600 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-xl font-medium transition flex items-center gap-2"
              >
                {loading ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <BrainCircuit size={16} />
                )}
                Analyze with AI
              </button>
            </div>

            {/* ── Saved Sessions Panel ── */}
            {(savedSessions.length > 0 || loadingSessions) && (
              <div className="mt-2">
                <button
                  onClick={() => setShowSavedSessions(!showSavedSessions)}
                  className="flex items-center gap-2 text-sm text-white/40 hover:text-white/60 transition mb-3"
                >
                  {showSavedSessions ? (
                    <ChevronUp size={14} />
                  ) : (
                    <ChevronDown size={14} />
                  )}
                  <FolderOpen size={14} />
                  Saved Sessions ({savedSessions.length})
                </button>
                {showSavedSessions && (
                  <div className="space-y-2">
                    {loadingSessions ? (
                      <div className="flex items-center justify-center p-6 text-white/30">
                        <Loader2 size={16} className="animate-spin mr-2" />{" "}
                        Loading sessions…
                      </div>
                    ) : (
                      savedSessions.map((s) => {
                        const stepLabel: Record<
                          string,
                          { text: string; color: string }
                        > = {
                          analyzed: {
                            text: "Analyzed",
                            color:
                              "bg-blue-500/15 text-blue-400 border-blue-500/30",
                          },
                          clarifying: {
                            text: "Clarifying",
                            color:
                              "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
                          },
                          clarified: {
                            text: "Clarified",
                            color:
                              "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
                          },
                          epics_generated: {
                            text: "Epics Ready",
                            color:
                              "bg-brand-500/15 text-brand-500 border-brand-500/30",
                          },
                          stories_generated: {
                            text: "Stories Done",
                            color:
                              "bg-green-500/15 text-green-400 border-green-500/30",
                          },
                        };
                        const badge = stepLabel[s.step] || stepLabel.analyzed;
                        const dateStr = s.updated_at
                          ? new Date(s.updated_at).toLocaleDateString(
                              undefined,
                              {
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              },
                            )
                          : "";
                        return (
                          <div
                            key={s.session_id}
                            className="flex items-center gap-3 p-3 bg-surface-200 border border-white/10 rounded-xl hover:border-white/20 transition group"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-sm text-white/80 font-medium truncate">
                                  {s.session_name}
                                </p>
                                <span
                                  className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-bold border ${badge.color}`}
                                >
                                  {badge.text}
                                </span>
                              </div>
                              <div className="flex items-center gap-3 mt-1 text-xs text-white/30">
                                <span>{s.requirement_count} reqs</span>
                                {s.epic_count > 0 && (
                                  <span>{s.epic_count} epics</span>
                                )}
                                {s.story_count > 0 && (
                                  <span>{s.story_count} stories</span>
                                )}
                                {s.total_points > 0 && (
                                  <span>{s.total_points} pts</span>
                                )}
                                {dateStr && (
                                  <span className="flex items-center gap-1">
                                    <Clock size={10} /> {dateStr}
                                  </span>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => handleResumeSession(s.session_id)}
                              disabled={loading}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-500/15 hover:bg-brand-500/25 border border-brand-500/30 text-brand-500 rounded-lg text-xs font-medium transition"
                            >
                              <Play size={12} /> Resume
                            </button>
                            <button
                              onClick={() => handleDeleteSession(s.session_id)}
                              className="p-1.5 text-white/20 hover:text-red-400 transition opacity-0 group-hover:opacity-100"
                              title="Delete session"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ═══════ STEP 2: AI Analysis & Clarification ═══════ */}
        {step === 2 && (
          <div className="space-y-6">
            {/* Document summary */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <h3 className="text-sm font-semibold text-white/80 mb-3 flex items-center gap-2">
                <BrainCircuit size={16} className="text-brand-500" /> AI
                Analysis Summary
              </h3>
              <p className="text-sm text-white/60 leading-relaxed">
                {documentSummary}
              </p>
              <div className="flex gap-4 mt-4">
                <div className="px-3 py-1.5 bg-blue-500/10 border border-blue-500/20 rounded-lg text-xs text-blue-400">
                  <span className="font-bold">{reqCounts.functional}</span>{" "}
                  Functional
                </div>
                <div className="px-3 py-1.5 bg-brand-500/10 border border-brand-500/20 rounded-lg text-xs text-brand-500">
                  <span className="font-bold">{reqCounts.non_functional}</span>{" "}
                  Non-Functional
                </div>
                <div className="px-3 py-1.5 bg-orange-500/10 border border-orange-500/20 rounded-lg text-xs text-orange-400">
                  <span className="font-bold">{reqCounts.constraints}</span>{" "}
                  Constraints
                </div>
                <div className="px-3 py-1.5 bg-teal-500/10 border border-teal-500/20 rounded-lg text-xs text-teal-400">
                  <span className="font-bold">{reqCounts.assumptions}</span>{" "}
                  Assumptions
                </div>
              </div>
            </div>

            {/* Requirements list */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <h3 className="text-sm font-semibold text-white/80 mb-4 flex items-center gap-2">
                <ClipboardList size={16} className="text-brand-500" /> Extracted
                Requirements ({requirements.length})
              </h3>
              <div className="space-y-2 max-h-64 overflow-y-auto custom-scrollbar">
                {requirements.map((req) => (
                  <div
                    key={req.id}
                    className="flex items-start gap-3 p-3 bg-white/5 rounded-xl border border-white/5"
                  >
                    <RequirementTypeBadge type={req.type} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white/70 font-medium">
                        {req.title}
                      </p>
                      <p className="text-xs text-white/40 mt-0.5 line-clamp-2">
                        {req.description}
                      </p>
                    </div>
                    <PriorityBadge priority={req.priority} />
                  </div>
                ))}
              </div>
            </div>

            {/* Clarifying questions */}
            {clarifications.length > 0 && (
              <div className="bg-white/5 border border-brand-500/20 rounded-2xl p-6">
                <h3 className="text-sm font-semibold text-white/80 mb-4 flex items-center gap-2">
                  <MessageSquare size={16} className="text-brand-500" />{" "}
                  Clarifying Questions ({clarifications.length})
                </h3>
                <p className="text-xs text-white/30 mb-4">
                  Answer these to improve the quality of generated epics and
                  stories. You can also skip if the defaults work.
                </p>
                <div className="space-y-4">
                  {clarifications.map((q) => (
                    <div key={q.id} className="space-y-2">
                      <div className="flex items-start gap-2">
                        <span className="mt-0.5 px-2 py-0.5 bg-brand-500/10 border border-brand-500/20 rounded text-[11px] text-brand-500 font-bold uppercase">
                          {q.category}
                        </span>
                        <p className="text-sm text-white/70">{q.question}</p>
                      </div>
                      {q.context && (
                        <p className="text-xs text-white/30 ml-16">
                          {q.context}
                        </p>
                      )}
                      <textarea
                        value={clarificationAnswers[q.id] || ""}
                        onChange={(e) =>
                          setClarificationAnswers((prev) => ({
                            ...prev,
                            [q.id]: e.target.value,
                          }))
                        }
                        placeholder="Your answer..."
                        className="w-full bg-surface-200 border border-white/10 rounded-xl p-3 text-sm text-white/80 placeholder-white/20 resize-none h-16 focus:border-brand-500/40 focus:outline-none transition"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-between">
              <button
                onClick={() => setStep(1)}
                className="px-4 py-2 text-sm text-white/40 hover:text-white/60 transition flex items-center gap-2"
              >
                <ArrowLeft size={14} /> Back
              </button>
              <div className="flex gap-3">
                {clarifications.length > 0 && (
                  <button
                    onClick={handleSkipClarifications}
                    disabled={loading}
                    className="px-4 py-2 text-sm text-white/40 hover:text-white/60 border border-white/10 rounded-xl transition"
                  >
                    Skip & Generate
                  </button>
                )}
                <button
                  onClick={
                    clarifications.length > 0
                      ? handleSubmitClarifications
                      : handleSkipClarifications
                  }
                  disabled={loading}
                  className="px-6 py-3 bg-brand-500 hover:bg-brand-600 disabled:opacity-40 text-white rounded-xl font-medium transition flex items-center gap-2"
                >
                  {loading ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <ArrowRight size={16} />
                  )}
                  {clarifications.length > 0
                    ? "Submit & Generate Epics"
                    : "Generate Epics"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ═══════ STEP 3: Epic Design ═══════ */}
        {step === 3 && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white/90 flex items-center gap-2">
                <Target size={20} className="text-brand-500" /> Epic Breakdown
                <span className="text-sm font-normal text-white/30">
                  ({selectedEpicIds.length}/{epics.length} selected)
                </span>
              </h3>
              <button
                onClick={handleGenerateEpics}
                disabled={loading}
                className="px-3 py-1.5 text-xs text-brand-500 border border-brand-500/30 rounded-lg hover:bg-brand-500/10 transition flex items-center gap-1"
              >
                <RefreshCw size={12} /> Regenerate
              </button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center space-y-3">
                  <div className="w-12 h-12 border-2 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto" />
                  <p className="text-sm text-white/60">
                    Designing epics from requirements...
                  </p>
                  <p className="text-xs text-white/25">
                    Using BMAD methodology for optimal decomposition
                  </p>
                </div>
              </div>
            ) : (
              <>
                {/* Select / Deselect All */}
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      const sel: Record<string, boolean> = {};
                      epics.forEach((e) => {
                        sel[e.id] = true;
                      });
                      setEpicSelections(sel);
                    }}
                    className="text-xs text-brand-500 hover:text-brand-400 transition"
                  >
                    Select All
                  </button>
                  <span className="text-white/10">|</span>
                  <button
                    onClick={() => {
                      const sel: Record<string, boolean> = {};
                      epics.forEach((e) => {
                        sel[e.id] = false;
                      });
                      setEpicSelections(sel);
                    }}
                    className="text-xs text-white/30 hover:text-white/50 transition"
                  >
                    Deselect All
                  </button>
                </div>

                {/* Epic cards with selection */}
                <div className="grid gap-3">
                  {epics.map((epic) => {
                    const selected = epicSelections[epic.id] !== false;
                    const isEditing = editingEpicId === epic.id;
                    return (
                      <div
                        key={epic.id}
                        className={`border rounded-2xl p-4 transition-all ${
                          selected
                            ? "bg-white/5 border-brand-500/20 hover:border-brand-500/40"
                            : "bg-white/[0.02] border-white/5 opacity-50"
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          {/* Checkbox */}
                          <button
                            onClick={() =>
                              setEpicSelections((prev) => ({
                                ...prev,
                                [epic.id]: !selected,
                              }))
                            }
                            className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition ${
                              selected
                                ? "bg-brand-500 border-brand-500 text-white"
                                : "border-white/20 hover:border-white/40"
                            }`}
                          >
                            {selected && <Check size={12} />}
                          </button>

                          {/* Epic key badge */}
                          <span className="px-2.5 py-1 bg-brand-500/20 text-brand-500 rounded-lg text-xs font-bold shrink-0">
                            {epic.key}
                          </span>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            {isEditing ? (
                              <div className="space-y-2">
                                <input
                                  value={editEpicForm.title}
                                  onChange={(e) =>
                                    setEditEpicForm((prev) => ({
                                      ...prev,
                                      title: e.target.value,
                                    }))
                                  }
                                  className="w-full bg-surface-200 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white/80 focus:border-brand-500/40 focus:outline-none"
                                  placeholder="Epic title"
                                />
                                <textarea
                                  value={editEpicForm.goal}
                                  onChange={(e) =>
                                    setEditEpicForm((prev) => ({
                                      ...prev,
                                      goal: e.target.value,
                                    }))
                                  }
                                  className="w-full bg-surface-200 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white/80 focus:border-brand-500/40 focus:outline-none resize-none"
                                  rows={2}
                                  placeholder="Epic goal"
                                />
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => {
                                      setEpics((prev) =>
                                        prev.map((e) =>
                                          e.id === epic.id
                                            ? {
                                                ...e,
                                                title: editEpicForm.title,
                                                goal: editEpicForm.goal,
                                              }
                                            : e,
                                        ),
                                      );
                                      setEditingEpicId(null);
                                    }}
                                    className="px-3 py-1 text-xs bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition flex items-center gap-1"
                                  >
                                    <Save size={10} /> Save
                                  </button>
                                  <button
                                    onClick={() => setEditingEpicId(null)}
                                    className="px-3 py-1 text-xs text-white/30 hover:text-white/60 transition"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <h4 className="text-sm font-semibold text-white/80">
                                  {epic.title}
                                </h4>
                                <p className="text-xs text-white/40 mt-1">
                                  {epic.goal}
                                </p>
                              </>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-2 shrink-0">
                            {!isEditing && (
                              <button
                                onClick={() => {
                                  setEditingEpicId(epic.id);
                                  setEditEpicForm({
                                    title: epic.title,
                                    goal: epic.goal,
                                  });
                                }}
                                className="p-1.5 text-white/20 hover:text-brand-500 hover:bg-white/5 rounded-lg transition"
                                title="Edit epic"
                              >
                                <Edit3 size={13} />
                              </button>
                            )}
                            <span className="text-[11px] text-white/20">
                              {epic.requirement_ids.length} reqs
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* FR Coverage */}
                {Object.keys(coverageMap).length > 0 && (
                  <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
                    <h4 className="text-sm font-semibold text-white/60 mb-3 flex items-center gap-2">
                      <CheckCircle2 size={14} className="text-green-400" />{" "}
                      Requirements Coverage Map
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {Object.entries(coverageMap).map(([reqId, epicIds]) => (
                        <div
                          key={reqId}
                          className="flex items-center gap-2 text-xs"
                        >
                          <span className="text-white/40">{reqId}</span>
                          <ArrowRight size={10} className="text-white/15" />
                          <span className="text-brand-500">
                            {epicIds.join(", ")}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Project Roles */}
                <div className="bg-surface-200 border border-white/10 rounded-2xl p-5">
                  <h4 className="text-sm font-semibold text-white/60 mb-2 flex items-center gap-2">
                    <Eye size={14} className="text-brand-500" /> Project Roles /
                    Personas
                  </h4>
                  <p className="text-xs text-white/30 mb-3">
                    Define the user roles for your project. These will be used
                    in the "As a [role]" part of user stories instead of generic
                    roles.
                  </p>
                  <div className="flex gap-2 mb-3">
                    <input
                      value={roleInput}
                      onChange={(e) => setRoleInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleAddRole()}
                      placeholder="e.g. NICU Nurse, Data Analyst, Clinical Admin..."
                      className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white/80 placeholder-white/20 focus:border-brand-500/40 focus:outline-none transition"
                    />
                    <button
                      onClick={handleAddRole}
                      disabled={!roleInput.trim()}
                      className="px-3 py-2 bg-brand-500/20 text-brand-500 border border-brand-500/30 rounded-lg hover:bg-brand-500/30 disabled:opacity-30 transition text-sm flex items-center gap-1"
                    >
                      <Plus size={14} /> Add
                    </button>
                  </div>
                  {projectRoles.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {projectRoles.map((role) => (
                        <span
                          key={role}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand-500/10 border border-brand-500/20 rounded-lg text-xs text-brand-500"
                        >
                          {role}
                          <button
                            onClick={() => handleRemoveRole(role)}
                            className="text-brand-500/50 hover:text-red-400 transition"
                          >
                            <X size={12} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  {projectRoles.length === 0 && (
                    <p className="text-[11px] text-white/15 italic">
                      No roles added — AI will infer roles from the document
                      context
                    </p>
                  )}
                </div>

                {/* Epic Feedback */}
                <div className="bg-surface-200 border border-white/10 rounded-2xl p-5">
                  <h4 className="text-sm font-semibold text-white/60 mb-2 flex items-center gap-2">
                    <MessageSquare size={14} className="text-brand-500" />{" "}
                    Additional Guidance (Optional)
                  </h4>
                  <p className="text-xs text-white/30 mb-3">
                    Share any additional guidance for story generation. For
                    example: specific focus areas, constraints, or priorities.
                  </p>
                  <textarea
                    className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-sm text-white/80 placeholder-white/20 focus:outline-none focus:ring-1 focus:ring-brand-500 resize-none"
                    rows={3}
                    placeholder='e.g. "Focus on MVP features first" or "Include error handling stories for each epic"'
                    value={epicFeedback}
                    onChange={(e) => setEpicFeedback(e.target.value)}
                  />
                </div>

                <div className="flex justify-between">
                  <button
                    onClick={() => setStep(2)}
                    className="px-4 py-2 text-sm text-white/40 hover:text-white/60 transition flex items-center gap-2"
                  >
                    <ArrowLeft size={14} /> Back
                  </button>
                  <button
                    onClick={handleGenerateAllStories}
                    disabled={loading || selectedEpicIds.length === 0}
                    className="px-6 py-3 bg-brand-500 hover:bg-brand-600 disabled:opacity-40 text-white rounded-xl font-medium transition flex items-center gap-2"
                  >
                    {loading ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Sparkles size={16} />
                    )}
                    Generate Stories for {selectedEpicIds.length} Epic
                    {selectedEpicIds.length !== 1 ? "s" : ""}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* ═══════ STEP 4 (merged into 5): Story Generation → Review ═══════ */}
        {step === 4 && (
          <div className="flex items-center justify-center py-20">
            <div className="text-center space-y-3">
              <div className="w-12 h-12 border-2 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="text-sm text-white/60">
                Generating user stories with BDD acceptance criteria...
              </p>
              {storyGenProgress.total > 0 && (
                <>
                  <p className="text-xs text-brand-500 font-medium">
                    Epic {storyGenProgress.current} of {storyGenProgress.total}:{" "}
                    {storyGenProgress.epicKey}
                  </p>
                  <div className="w-48 mx-auto bg-white/5 rounded-full h-1.5 overflow-hidden">
                    <div
                      className="bg-brand-500 h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${(storyGenProgress.current / storyGenProgress.total) * 100}%`,
                      }}
                    />
                  </div>
                </>
              )}
              <p className="text-xs text-white/25">
                Creating tasks, subtasks, and estimating story points
              </p>
            </div>
          </div>
        )}

        {/* ═══════ STEP 5: Review & Edit ═══════ */}
        {step === 5 && (
          <div className="flex gap-6 h-[calc(100vh-280px)]">
            {/* Left: Backlog Board */}
            <div className="flex-1 overflow-y-auto custom-scrollbar space-y-4">
              {/* Summary bar */}
              <div className="flex items-center gap-4 p-4 bg-white/5 border border-white/10 rounded-2xl">
                <div className="flex items-center gap-2 text-sm">
                  <Target size={14} className="text-brand-500" />
                  <span className="text-white/60">
                    <span className="font-bold text-white/80">
                      {epics.length}
                    </span>{" "}
                    Epics
                  </span>
                </div>
                <div className="w-px h-4 bg-white/10" />
                <div className="flex items-center gap-2 text-sm">
                  <ListChecks size={14} className="text-brand-500" />
                  <span className="text-white/60">
                    <span className="font-bold text-white/80">
                      {totalStories}
                    </span>{" "}
                    Stories
                  </span>
                </div>
                <div className="w-px h-4 bg-white/10" />
                <div className="flex items-center gap-2 text-sm">
                  <Zap size={14} className="text-brand-500" />
                  <span className="text-white/60">
                    <span className="font-bold text-white/80">
                      {totalPoints}
                    </span>{" "}
                    Points
                  </span>
                </div>
                <div className="flex-1" />
                <button
                  onClick={() => setStep(6)}
                  className="px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white rounded-lg text-sm font-medium transition flex items-center gap-2"
                >
                  <Download size={14} /> Export
                </button>
              </div>

              {/* Epics with stories */}
              {epics.map((epic) => (
                <div
                  key={epic.id}
                  className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden"
                >
                  {/* Epic header */}
                  <button
                    onClick={() =>
                      setExpandedEpic(expandedEpic === epic.id ? null : epic.id)
                    }
                    className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition"
                  >
                    <div className="flex items-center gap-3">
                      {expandedEpic === epic.id ? (
                        <ChevronDown size={16} className="text-white/30" />
                      ) : (
                        <ChevronRight size={16} className="text-white/30" />
                      )}
                      <span className="px-2 py-0.5 bg-brand-500/20 text-brand-500 rounded text-xs font-bold">
                        {epic.key}
                      </span>
                      <span className="text-sm font-semibold text-white/80">
                        {epic.title}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-white/30">
                      <span>{epic.stories.length} stories</span>
                      <span className="px-2 py-0.5 bg-white/5 rounded font-mono">
                        {epic.total_points} pts
                      </span>
                    </div>
                  </button>

                  {/* Stories */}
                  {expandedEpic === epic.id && (
                    <div className="border-t border-white/5">
                      {epic.stories.map((story) => (
                        <div
                          key={story.id}
                          className="border-b border-white/5 last:border-0"
                        >
                          {/* Story header */}
                          <button
                            onClick={() =>
                              setExpandedStory(
                                expandedStory === story.id ? null : story.id,
                              )
                            }
                            className="w-full flex items-center justify-between px-6 py-3 hover:bg-white/5 transition"
                          >
                            <div className="flex items-center gap-3">
                              {expandedStory === story.id ? (
                                <ChevronDown
                                  size={14}
                                  className="text-white/20"
                                />
                              ) : (
                                <ChevronRight
                                  size={14}
                                  className="text-white/20"
                                />
                              )}
                              <span className="text-xs font-mono text-white/30">
                                {story.key}
                              </span>
                              <span className="text-sm text-white/70">
                                {story.title}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <PriorityBadge priority={story.priority} />
                              <StoryPointBadge
                                points={story.story_points}
                                confidence={story.estimation_confidence}
                              />
                            </div>
                          </button>

                          {/* Story detail — improved layout */}
                          {expandedStory === story.id && (
                            <div className="px-6 pb-5 space-y-4">
                              {/* User Story Statement */}
                              <div className="bg-brand-500/5 rounded-xl p-4 border border-brand-500/10">
                                <div className="text-[11px] uppercase tracking-wider text-brand-500/60 font-bold mb-2">
                                  User Story
                                </div>
                                <p className="text-sm text-white/70 leading-relaxed">
                                  <span className="text-white/40">As a</span>{" "}
                                  <span className="text-brand-500 font-semibold">
                                    {story.as_a}
                                  </span>
                                  ,{" "}
                                  <span className="text-white/40">I want</span>{" "}
                                  <span className="text-white/80 font-medium">
                                    {story.i_want}
                                  </span>
                                  ,{" "}
                                  <span className="text-white/40">so that</span>{" "}
                                  <span className="text-white/80 font-medium">
                                    {story.so_that}
                                  </span>
                                  .
                                </p>
                              </div>

                              {/* Two-column: AC + Tasks */}
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                {/* Acceptance Criteria */}
                                <div>
                                  <h5 className="text-[11px] font-bold text-white/40 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                    <CheckCircle2
                                      size={11}
                                      className="text-green-400"
                                    />{" "}
                                    Acceptance Criteria
                                  </h5>
                                  <div className="space-y-2">
                                    {story.acceptance_criteria.map((ac, i) => {
                                      const scenarioColors: Record<
                                        string,
                                        string
                                      > = {
                                        happy_path:
                                          "bg-green-500/15 text-green-400 border-green-500/30",
                                        edge_case:
                                          "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
                                        error_handling:
                                          "bg-red-500/15 text-red-400 border-red-500/30",
                                        boundary:
                                          "bg-purple-500/15 text-purple-400 border-purple-500/30",
                                      };
                                      const scenarioLabel = ac.scenario
                                        ? ac.scenario.replace("_", " ")
                                        : null;
                                      return (
                                        <div
                                          key={ac.id}
                                          className="bg-white/[0.03] rounded-lg p-3 border border-white/5 text-xs"
                                        >
                                          <div className="flex items-center gap-2 mb-1.5">
                                            <span className="text-[11px] text-white/20 font-bold">
                                              AC {i + 1}
                                            </span>
                                            {scenarioLabel && (
                                              <span
                                                className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-bold border uppercase ${scenarioColors[ac.scenario!] || scenarioColors.happy_path}`}
                                              >
                                                {scenarioLabel}
                                              </span>
                                            )}
                                          </div>
                                          <div className="space-y-1">
                                            <p className="text-white/60 leading-relaxed">
                                              <span className="text-green-400 font-bold">
                                                Given
                                              </span>{" "}
                                              {ac.given}
                                            </p>
                                            <p className="text-white/60 leading-relaxed">
                                              <span className="text-blue-400 font-bold">
                                                When
                                              </span>{" "}
                                              {ac.when}
                                            </p>
                                            <p className="text-white/60 leading-relaxed">
                                              <span className="text-brand-500 font-bold">
                                                Then
                                              </span>{" "}
                                              {ac.then}
                                            </p>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>

                                {/* Tasks */}
                                {story.tasks.length > 0 && (
                                  <div>
                                    <h5 className="text-[11px] font-bold text-white/40 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                      <ListChecks
                                        size={11}
                                        className="text-brand-500"
                                      />{" "}
                                      Tasks & Subtasks
                                    </h5>
                                    <div className="space-y-2">
                                      {story.tasks.map((task) => (
                                        <div
                                          key={task.id}
                                          className="bg-white/[0.03] rounded-lg p-3 border border-white/5"
                                        >
                                          <div className="flex items-start gap-2 text-xs">
                                            <div className="w-4 h-4 border-2 border-white/15 rounded mt-0.5 shrink-0" />
                                            <div className="flex-1">
                                              <span className="text-white/70 font-medium">
                                                {task.title}
                                              </span>
                                              {task.linked_ac.length > 0 && (
                                                <span className="text-white/15 ml-1.5 text-[11px]">
                                                  (AC:{" "}
                                                  {task.linked_ac.join(", ")})
                                                </span>
                                              )}
                                              {task.subtasks.length > 0 && (
                                                <ul className="mt-1.5 space-y-1">
                                                  {task.subtasks.map(
                                                    (st, j) => (
                                                      <li
                                                        key={j}
                                                        className="text-white/40 text-[11px] flex items-center gap-1.5 pl-1"
                                                      >
                                                        <div className="w-2.5 h-2.5 border border-white/10 rounded shrink-0" />
                                                        {st}
                                                      </li>
                                                    ),
                                                  )}
                                                </ul>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Definition of Ready */}
                                {story.definition_of_ready &&
                                  story.definition_of_ready.length > 0 && (
                                    <div>
                                      <h5 className="text-[11px] font-bold text-white/40 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                        <CheckCircle2
                                          size={11}
                                          className="text-teal-400"
                                        />{" "}
                                        Definition of Ready
                                      </h5>
                                      <div className="flex flex-wrap gap-1.5">
                                        {story.definition_of_ready.map(
                                          (item, i) => (
                                            <span
                                              key={i}
                                              className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-500/10 border border-teal-500/20 rounded text-[11px] text-teal-400"
                                            >
                                              <Check size={9} /> {item}
                                            </span>
                                          ),
                                        )}
                                      </div>
                                    </div>
                                  )}
                              </div>

                              {/* Footer: Estimation + Dependencies + Actions */}
                              <div className="flex items-center justify-between pt-2 border-t border-white/5">
                                <div className="flex items-center gap-3 text-xs">
                                  <div className="flex items-center gap-1.5">
                                    <StoryPointBadge
                                      points={story.story_points}
                                      confidence={story.estimation_confidence}
                                    />
                                  </div>
                                  <span
                                    className="text-white/20 max-w-xs truncate"
                                    title={story.estimation_reasoning}
                                  >
                                    {story.estimation_reasoning}
                                  </span>
                                  {story.dependencies.length > 0 && (
                                    <>
                                      <span className="text-white/10">|</span>
                                      <span className="text-white/25">
                                        Deps: {story.dependencies.join(", ")}
                                      </span>
                                    </>
                                  )}
                                </div>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => {
                                      setEditingStory(story.id);
                                      setEditForm(story);
                                    }}
                                    className="px-3 py-1.5 text-xs text-brand-500 border border-brand-500/30 rounded-lg hover:bg-brand-500/10 transition flex items-center gap-1"
                                  >
                                    <Edit3 size={12} /> Edit
                                  </button>
                                  <button
                                    onClick={() => handleDeleteStory(story.id)}
                                    className="px-3 py-1.5 text-xs text-red-400 border border-red-500/30 rounded-lg hover:bg-red-500/10 transition flex items-center gap-1"
                                  >
                                    <Trash2 size={12} /> Remove
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              <div className="flex justify-between pt-4">
                <button
                  onClick={() => setStep(3)}
                  className="px-4 py-2 text-sm text-white/40 hover:text-white/60 transition flex items-center gap-2"
                >
                  <ArrowLeft size={14} /> Back to Epics
                </button>
              </div>
            </div>

            {/* Right: Chat panel */}
            <div className="w-96 flex flex-col bg-white/5 border border-white/10 rounded-2xl overflow-hidden shrink-0">
              <div className="px-4 py-3 border-b border-white/5">
                <h4 className="text-sm font-semibold text-white/60 flex items-center gap-2">
                  <MessageSquare size={14} className="text-brand-500" /> Refine
                  with AI
                </h4>
                <p className="text-[11px] text-white/25 mt-0.5">
                  Ask to modify stories, add details, split or merge epics
                </p>
              </div>
              <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-3">
                {chatMessages.length === 0 && (
                  <div className="text-center py-8">
                    <BrainCircuit
                      size={32}
                      className="mx-auto text-white/10 mb-3"
                    />
                    <p className="text-xs text-white/20">
                      Ask me to refine your backlog
                    </p>
                    <div className="mt-4 space-y-2">
                      {[
                        "Split story X into smaller stories",
                        "Add more acceptance criteria to Epic 1",
                        "Re-estimate story points for all stories",
                        "Add a new story for error handling",
                      ].map((s, i) => (
                        <button
                          key={i}
                          onClick={() => setChatInput(s)}
                          className="block w-full text-left text-[11px] text-white/30 hover:text-brand-500 px-3 py-1.5 rounded-lg hover:bg-white/5 transition"
                        >
                          "{s}"
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {chatMessages.map((msg, i) => (
                  <div
                    key={i}
                    className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[85%] px-3 py-2 rounded-xl text-xs ${
                        msg.role === "user"
                          ? "bg-brand-500/20 text-white/80"
                          : "bg-white/5 text-white/60"
                      }`}
                    >
                      {msg.content}
                    </div>
                  </div>
                ))}
                {chatLoading && (
                  <div className="flex justify-start">
                    <div className="bg-white/5 px-3 py-2 rounded-xl">
                      <Loader2
                        size={14}
                        className="animate-spin text-brand-500"
                      />
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>
              <div className="p-3 border-t border-white/5">
                <div className="flex gap-2">
                  <input
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) =>
                      e.key === "Enter" && !e.shiftKey && handleChatSend()
                    }
                    placeholder="Ask to refine..."
                    className="flex-1 bg-surface-200 border border-white/10 rounded-lg px-3 py-2 text-xs text-white/80 placeholder-white/20 focus:border-brand-500/40 focus:outline-none transition"
                  />
                  <button
                    onClick={handleChatSend}
                    disabled={chatLoading || !chatInput.trim()}
                    className="p-2 bg-brand-500 hover:bg-brand-600 disabled:opacity-40 rounded-lg transition"
                  >
                    <Send size={14} className="text-white" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ═══════ STEP 6: Export ═══════ */}
        {step === 6 && (
          <div className="max-w-2xl mx-auto space-y-6">
            <div className="flex items-center gap-3 mb-2">
              <button
                onClick={() => setStep(5)}
                className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/40 hover:text-white transition"
              >
                <ArrowLeft size={16} />
              </button>
              <h3 className="text-lg font-semibold text-white/90 flex items-center gap-2">
                <Download size={20} className="text-brand-500" /> Export Backlog
              </h3>
            </div>

            {/* Summary */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-brand-500">
                    {epics.length}
                  </div>
                  <div className="text-xs text-white/30">Epics</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-brand-500">
                    {totalStories}
                  </div>
                  <div className="text-xs text-white/30">User Stories</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-brand-500">
                    {totalPoints}
                  </div>
                  <div className="text-xs text-white/30">Story Points</div>
                </div>
              </div>
            </div>

            {/* Format selection */}
            <div className="grid grid-cols-2 gap-3">
              {[
                {
                  id: "excel",
                  label: "Excel (.xlsx)",
                  desc: "Formatted spreadsheet with epics, stories, AC, and estimates",
                  icon: FileSpreadsheet,
                },
                {
                  id: "word",
                  label: "Word (.docx)",
                  desc: "Professional document with full backlog details",
                  icon: FileText,
                },
                {
                  id: "jira",
                  label: "JIRA",
                  desc: "Create epics and stories directly in your JIRA project",
                  icon: ExternalLink,
                },
                {
                  id: "github",
                  label: "GitHub Issues",
                  desc: "Create issues with labels and milestones in your repository",
                  icon: ExternalLink,
                },
              ].map((fmt) => (
                <button
                  key={fmt.id}
                  onClick={() => setExportFormat(fmt.id)}
                  className={`p-4 rounded-2xl border text-left transition ${
                    exportFormat === fmt.id
                      ? "bg-brand-500/10 border-brand-500/40 text-brand-500"
                      : "bg-white/5 border-white/10 text-white/40 hover:border-white/20"
                  }`}
                >
                  <fmt.icon size={20} className="mb-2" />
                  <div className="text-sm font-medium">{fmt.label}</div>
                  <div className="text-[11px] mt-1 opacity-60">{fmt.desc}</div>
                </button>
              ))}
            </div>

            {/* JIRA Config */}
            {exportFormat === "jira" && (
              <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-3">
                <h4 className="text-sm font-semibold text-white/60">
                  JIRA Configuration
                </h4>
                {[
                  {
                    key: "base_url",
                    label: "JIRA URL",
                    placeholder: "https://your-org.atlassian.net",
                  },
                  {
                    key: "email",
                    label: "Email",
                    placeholder: "your-email@company.com",
                  },
                  {
                    key: "api_token",
                    label: "API Token",
                    placeholder: "Your JIRA API token",
                    type: "password",
                  },
                  {
                    key: "project_key",
                    label: "Project Key",
                    placeholder: "e.g. PROJ",
                  },
                ].map((field) => (
                  <div key={field.key}>
                    <label className="block text-xs text-white/30 mb-1">
                      {field.label}
                    </label>
                    <input
                      type={field.type || "text"}
                      value={(jiraConfig as any)[field.key]}
                      onChange={(e) =>
                        setJiraConfig((prev) => ({
                          ...prev,
                          [field.key]: e.target.value,
                        }))
                      }
                      placeholder={field.placeholder}
                      className="w-full bg-surface-200 border border-white/10 rounded-lg px-3 py-2 text-sm text-white/80 placeholder-white/20 focus:border-brand-500/40 focus:outline-none transition"
                    />
                  </div>
                ))}
              </div>
            )}

            {/* GitHub Config */}
            {exportFormat === "github" && (
              <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-3">
                <h4 className="text-sm font-semibold text-white/60">
                  GitHub Configuration
                </h4>
                {[
                  {
                    key: "token",
                    label: "Personal Access Token",
                    placeholder: "ghp_...",
                    type: "password",
                  },
                  {
                    key: "owner",
                    label: "Owner / Organization",
                    placeholder: "e.g. my-org",
                  },
                  {
                    key: "repo",
                    label: "Repository",
                    placeholder: "e.g. my-project",
                  },
                ].map((field) => (
                  <div key={field.key}>
                    <label className="block text-xs text-white/30 mb-1">
                      {field.label}
                    </label>
                    <input
                      type={field.type || "text"}
                      value={(githubConfig as any)[field.key]}
                      onChange={(e) =>
                        setGithubConfig((prev) => ({
                          ...prev,
                          [field.key]: e.target.value,
                        }))
                      }
                      placeholder={field.placeholder}
                      className="w-full bg-surface-200 border border-white/10 rounded-lg px-3 py-2 text-sm text-white/80 placeholder-white/20 focus:border-brand-500/40 focus:outline-none transition"
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Export button */}
            <button
              onClick={handleExport}
              disabled={loading}
              className="w-full px-6 py-3 bg-brand-500 hover:bg-brand-600 disabled:opacity-40 text-white rounded-xl font-medium transition flex items-center justify-center gap-2"
            >
              {loading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Download size={16} />
              )}
              {exportFormat === "excel"
                ? "Download Excel"
                : exportFormat === "word"
                  ? "Download Word"
                  : exportFormat === "jira"
                    ? "Push to JIRA"
                    : "Push to GitHub"}
            </button>

            {/* Export result */}
            {exportResult && (
              <div
                className={`p-4 rounded-2xl border ${exportResult.errors?.length ? "bg-orange-500/10 border-orange-500/30" : "bg-green-500/10 border-green-500/30"}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  {exportResult.errors?.length ? (
                    <AlertTriangle size={16} className="text-orange-400" />
                  ) : (
                    <CheckCircle2 size={16} className="text-green-400" />
                  )}
                  <span className="text-sm font-medium text-white/80">
                    {exportResult.items_created} items created
                  </span>
                </div>
                {exportResult.details.length > 0 && (
                  <div className="space-y-1 max-h-40 overflow-y-auto custom-scrollbar">
                    {exportResult.details.map((d, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <span className="text-white/30">{d.key}</span>
                        <span className="text-white/50">{d.title}</span>
                        {d.url && (
                          <a
                            href={d.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-brand-500 hover:text-brand-400"
                          >
                            <ExternalLink size={10} />
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {exportResult.errors?.map((err, i) => (
                  <p key={i} className="text-xs text-red-400 mt-1">
                    {err}
                  </p>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ═══════ Edit Story Modal ═══════ */}
        {editingStory && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center"
            style={{
              background: "rgba(0,0,0,0.7)",
              backdropFilter: "blur(4px)",
            }}
          >
            <div className="bg-surface-400 border border-white/10 rounded-2xl p-6 w-[600px] max-h-[80vh] overflow-y-auto custom-scrollbar">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-semibold text-white/80">
                  Edit Story: {editForm.key}
                </h4>
                <button
                  onClick={() => {
                    setEditingStory(null);
                    setEditForm({});
                  }}
                  className="text-white/30 hover:text-white/60"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs text-white/30 mb-1">
                    Title
                  </label>
                  <input
                    value={editForm.title || ""}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        title: e.target.value,
                      }))
                    }
                    className="w-full bg-surface-200 border border-white/10 rounded-lg px-3 py-2 text-sm text-white/80 focus:border-brand-500/40 focus:outline-none transition"
                  />
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-white/30 mb-1">
                      As a...
                    </label>
                    <input
                      value={editForm.as_a || ""}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          as_a: e.target.value,
                        }))
                      }
                      className="w-full bg-surface-200 border border-white/10 rounded-lg px-3 py-2 text-sm text-white/80 focus:border-brand-500/40 focus:outline-none transition"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-white/30 mb-1">
                      I want...
                    </label>
                    <input
                      value={editForm.i_want || ""}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          i_want: e.target.value,
                        }))
                      }
                      className="w-full bg-surface-200 border border-white/10 rounded-lg px-3 py-2 text-sm text-white/80 focus:border-brand-500/40 focus:outline-none transition"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-white/30 mb-1">
                      So that...
                    </label>
                    <input
                      value={editForm.so_that || ""}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          so_that: e.target.value,
                        }))
                      }
                      className="w-full bg-surface-200 border border-white/10 rounded-lg px-3 py-2 text-sm text-white/80 focus:border-brand-500/40 focus:outline-none transition"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-white/30 mb-1">
                      Story Points
                    </label>
                    <select
                      value={editForm.story_points || 0}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          story_points: parseInt(e.target.value),
                        }))
                      }
                      className="w-full bg-surface-200 border border-white/10 rounded-lg px-3 py-2 text-sm text-white/80 focus:border-brand-500/40 focus:outline-none transition"
                    >
                      {[1, 2, 3, 5, 8, 13, 21].map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-white/30 mb-1">
                      Priority
                    </label>
                    <select
                      value={editForm.priority || "should"}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          priority: e.target.value as any,
                        }))
                      }
                      className="w-full bg-surface-200 border border-white/10 rounded-lg px-3 py-2 text-sm text-white/80 focus:border-brand-500/40 focus:outline-none transition"
                    >
                      {["must", "should", "could"].map((p) => (
                        <option key={p} value={p}>
                          {p.toUpperCase()}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-white/5">
                  <button
                    onClick={() => {
                      setEditingStory(null);
                      setEditForm({});
                    }}
                    className="px-4 py-2 text-sm text-white/40 hover:text-white/60 transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleSaveStory(editingStory)}
                    disabled={loading}
                    className="px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white rounded-lg text-sm font-medium transition flex items-center gap-2"
                  >
                    {loading ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Save size={14} />
                    )}
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
